<script setup lang="ts">
import { computed, ref, type ComponentPublicInstance } from "vue";

import { useToastStore, type ToastItem } from "@/store/toast.store";

const MAX_VISIBLE_TOASTS = 3;
const STACK_OFFSET_PX = 14;
const STACK_EXPANDED_GAP_PX = 80;
const STACK_SCALE_STEP = 0.04;
const STACK_OPACITY_STEP = 0.12;

interface ToastComponentApi extends ComponentPublicInstance {
  clearDismissTimeout?: () => void;
  setDismissTimeout?: () => void;
}

const toastStore = useToastStore();
const toastRefs = new Map<string, ToastComponentApi>();
const stackRef = ref<HTMLElement | null>(null);
const isExpanded = ref(false);

const visibleToasts = computed(() => toastStore.toasts.slice(-MAX_VISIBLE_TOASTS).reverse());

const stackContainerStyle = computed(() => {
  const visibleCount = visibleToasts.value.length;
  const hasBarStyleToast = visibleToasts.value.some((toast) => toast.forceBarStyle);

  return {
    "--toast-stack-hit-width": hasBarStyleToast
      ? "min(30rem, calc(100vw - 2rem))"
      : "min(22.5rem, calc(100vw - 2rem))",
    "--toast-stack-hit-height": `${Math.max(visibleCount - 1, 0) * STACK_EXPANDED_GAP_PX + 96}px`,
  };
});

function getStackStyle(index: number) {
  return {
    "--toast-collapsed-offset": `${index * STACK_OFFSET_PX}px`,
    "--toast-expanded-offset": `${index * STACK_EXPANDED_GAP_PX}px`,
    "--toast-collapsed-scale": String(1 - index * STACK_SCALE_STEP),
    "--toast-collapsed-opacity": String(1 - index * STACK_OPACITY_STEP),
    zIndex: String(MAX_VISIBLE_TOASTS - index),
  };
}

function setToastRef(id: string, instance: Element | ComponentPublicInstance | null): void {
  if (instance) {
    toastRefs.set(id, instance as ToastComponentApi);
    return;
  }

  toastRefs.delete(id);
}

function pauseToastTimers(): void {
  toastRefs.forEach((toastRef) => {
    toastRef.clearDismissTimeout?.();
  });
}

function resumeToastTimers(): void {
  toastRefs.forEach((toastRef) => {
    toastRef.setDismissTimeout?.();
  });
}

function isWithinStack(target: EventTarget | null): boolean {
  return target instanceof Node && !!stackRef.value?.contains(target);
}

function expandStack(): void {
  isExpanded.value = true;
  pauseToastTimers();
}

function collapseStack(): void {
  isExpanded.value = false;
  resumeToastTimers();
}

function handleToastMouseLeave(event: MouseEvent): void {
  if (isWithinStack(event.relatedTarget)) {
    return;
  }

  collapseStack();
}

function handleToastFocusOut(event: FocusEvent): void {
  if (isWithinStack(event.relatedTarget)) {
    return;
  }

  collapseStack();
}

function handleActionClick(toast: ToastItem): void {
  if (!toast.action) {
    return;
  }

  void toast.action.onClick();
  toastStore.removeToast(toast.id);
}
</script>

<template>
  <Teleport to="body">
    <div
      v-if="visibleToasts.length"
      ref="stackRef"
      class="toast-list"
      :class="{ 'toast-list--expanded': isExpanded }"
      :style="stackContainerStyle"
    >
      <div
        v-if="isExpanded"
        class="toast-list__hover-area"
        @mouseenter="expandStack"
        @mouseleave="collapseStack"
      />

      <TransitionGroup name="toast-stack" appear>
        <div
          v-for="(toast, index) in visibleToasts"
          :key="toast.id"
          class="toast-list__item"
          :style="getStackStyle(index)"
        >
          <Toast
            :ref="
              (instance: Element | ComponentPublicInstance | null) =>
                setToastRef(toast.id, instance)
            "
            :text="toast.text"
            :is-open="true"
            :action-button-text="toast.action?.label ?? ''"
            :aria-label-close-btn="toast.ariaLabelCloseBtn"
            :force-bar-style="toast.forceBarStyle"
            :on-close-request="() => toastStore.removeToast(toast.id)"
            :action-click="() => handleActionClick(toast)"
            @mouseenter="expandStack"
            @mouseleave="handleToastMouseLeave"
            @focusin="expandStack"
            @focusout="handleToastFocusOut"
          />
        </div>
      </TransitionGroup>
    </div>
  </Teleport>
</template>

<style scoped>
.toast-list {
  position: fixed;
  top: 2rem;
  right: 1rem;
  z-index: 1000;
  width: var(--toast-stack-hit-width);
  min-height: var(--toast-stack-hit-height);
  pointer-events: none;
}

.toast-list__hover-area {
  position: absolute;
  top: 0;
  right: 0;
  width: 100%;
  min-height: 100%;
  pointer-events: auto;
}

.toast-list__item {
  position: relative;
  pointer-events: none;
}

.toast-list__item :deep(.toast) {
  top: 2rem;
  right: 1rem;
  left: auto;
  width: min(22.5rem, calc(100vw - 2rem));
  opacity: 0;
  transform: translate3d(0, calc(-100% - 2rem), 0) scale(var(--toast-collapsed-scale));
  transform-origin: top center;
  transition:
    transform 320ms cubic-bezier(0.22, 1, 0.36, 1),
    opacity 220ms ease;
  pointer-events: none;
}

.toast-list__item :deep(.toast--show) {
  opacity: var(--toast-collapsed-opacity);
  transform: translate3d(0, var(--toast-collapsed-offset), 0) scale(var(--toast-collapsed-scale));
  pointer-events: auto;
}

.toast-list--expanded .toast-list__item :deep(.toast--show) {
  opacity: 1;
  transform: translate3d(0, var(--toast-expanded-offset), 0) scale(1);
}

.toast-list__item :deep(.toast--bar-type) {
  width: min(30rem, calc(100vw - 2rem));
  left: auto;
  right: 1rem;
}

.toast-list--expanded .toast-list__item :deep(.toast--bar-type.toast--show) {
  transform: translate3d(0, var(--toast-expanded-offset), 0) scale(1);
}

.toast-stack-enter-active :deep(.toast),
.toast-stack-leave-active :deep(.toast),
.toast-stack-move :deep(.toast) {
  transition:
    transform 320ms cubic-bezier(0.22, 1, 0.36, 1),
    opacity 220ms ease;
}

.toast-stack-enter-from :deep(.toast--show),
.toast-stack-leave-to :deep(.toast--show) {
  opacity: 0;
  transform: translate3d(0, calc(-100% - 2rem), 0) scale(var(--toast-collapsed-scale));
}

.toast-stack-enter-to :deep(.toast--show),
.toast-stack-leave-from :deep(.toast--show) {
  opacity: var(--toast-collapsed-opacity);
  transform: translate3d(0, var(--toast-collapsed-offset), 0) scale(var(--toast-collapsed-scale));
}
</style>
